import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MapPin, MessageSquare, Plus } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Spinner, EmptyState } from "@/components/shared";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function CommunityPage() {
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const [topics, setTopics] = useState([]);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", topic: "", location: "" });
  const active = sp.get("topic") || "";

  useEffect(() => { api.get("/community/topics").then((r) => setTopics(r.data)).catch(() => {}); }, []);

  const load = useCallback(() => {
    setData(null);
    const params = new URLSearchParams();
    if (active) params.set("topic", active);
    api.get(`/community/posts?${params.toString()}`).then((r) => setData(r.data)).catch(() => setData({ items: [] }));
  }, [active]);
  useEffect(() => { load(); }, [load]);

  const setTopic = (t) => { const n = new URLSearchParams(sp); if (t) n.set("topic", t); else n.delete("topic"); setSp(n); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/community/posts", { ...form, topic: form.topic || "community-questions" });
      toast.success("Posted to the community");
      setOpen(false); setForm({ title: "", body: "", topic: "", location: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="propora-container py-10" data-testid="community-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#007F83] mb-2">Community</p>
          <h1 className="font-display font-extrabold text-4xl text-[#0B1733]">Conversations that matter</h1>
          <p className="text-[#4A5568] mt-2 max-w-2xl">Get advice, ask questions and learn from the Dubai property community.</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { if (o && !user) { toast("Log in to start a discussion"); return; } setOpen(o); }}>
          <DialogTrigger asChild>
            <Button data-testid="new-post-btn" className="rounded-full bg-[#007F83] hover:bg-[#006B70] text-white"><Plus size={18} className="mr-1" /> New discussion</Button>
          </DialogTrigger>
          <DialogContent data-testid="new-post-dialog">
            <DialogHeader><DialogTitle className="font-display">Start a discussion</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="post-title-input" className="mt-1.5" /></div>
              <div><Label>Topic</Label>
                <Select value={form.topic} onValueChange={(v) => setForm({ ...form, topic: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="post-topic-select"><SelectValue placeholder="Choose a topic" /></SelectTrigger>
                  <SelectContent>{topics.map((t) => <SelectItem key={t.slug} value={t.slug}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Your question or post</Label><Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} required rows={5} data-testid="post-body-input" className="mt-1.5" /></div>
              <Button type="submit" data-testid="post-submit-btn" className="w-full rounded-full bg-[#007F83] hover:bg-[#006B70] text-white">Post</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-7 flex flex-wrap gap-2" data-testid="topic-filter">
        <button onClick={() => setTopic("")} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${!active ? "bg-[#0B1733] text-white" : "bg-white border border-[#E3E8E8] text-[#0B1733] hover:bg-[#E8F5F1]"}`}>All</button>
        {topics.map((t) => (
          <button key={t.slug} onClick={() => setTopic(t.slug)} data-testid={`topic-${t.slug}`}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${active === t.slug ? "bg-[#0B1733] text-white" : "bg-white border border-[#E3E8E8] text-[#0B1733] hover:bg-[#E8F5F1]"}`}>
            {t.name}
          </button>
        ))}
      </div>

      <div className="mt-6 space-y-4">
        {!data ? <Spinner /> : data.items.length === 0 ? (
          <EmptyState title="No discussions yet" subtitle="Be the first to start a conversation on this topic." />
        ) : data.items.map((p) => (
          <Link key={p.id} to={`/community/posts/${p.id}`} data-testid={`post-${p.id}`} className="block bg-white rounded-2xl border border-[#E3E8E8] p-6 card-lift">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-[#007F83] bg-[#E8F5F1] px-2 py-0.5 rounded">{p.topic}</span>
              {p.location && <span className="text-xs text-[#4A5568] inline-flex items-center gap-1"><MapPin size={12} /> {p.location}</span>}
            </div>
            <h3 className="font-display font-bold text-xl text-[#0B1733]">{p.title}</h3>
            <p className="mt-2 text-[#4A5568] line-clamp-2">{p.body}</p>
            <div className="mt-3 flex items-center gap-3 text-xs text-[#4A5568]">
              <span className="font-semibold text-[#0B1733]">{p.author}</span>
              <span className="inline-flex items-center gap-1"><MessageSquare size={13} /> {p.reply_count} replies</span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
